import axios from 'axios';
import { devWarn } from './log';

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const DEFAULT_CONTACT = {
  email: 'hello@melopainfra.com',
  phone: '+91 79751 27454',
  whatsapp: '917975127454',
};

// ---------------- UTM capture ----------------
export function captureUTM() {
  try {
    const params = new URLSearchParams(window.location.search);
    const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
    const existing = JSON.parse(sessionStorage.getItem('melopa_utm') || '{}');
    let found = false;
    const utm = { ...existing };
    keys.forEach((k) => {
      const v = params.get(k);
      if (v) {
        utm[k] = v;
        found = true;
      }
    });
    if (!sessionStorage.getItem('melopa_landing_page')) {
      sessionStorage.setItem('melopa_landing_page', window.location.pathname + window.location.search);
    }
    if (found || Object.keys(existing).length === 0) {
      sessionStorage.setItem('melopa_utm', JSON.stringify(utm));
    }
  } catch (e) {
    devWarn('UTM capture failed:', e);
  }
}

export function getDeviceType() {
  const ua = navigator.userAgent || '';
  if (/iPad|Tablet|PlayBook|Silk/i.test(ua)) return 'tablet';
  if (/Mobi|Android|iPhone/i.test(ua)) return 'mobile';
  return 'desktop';
}

export function getAttribution() {
  let utm = {};
  try {
    utm = JSON.parse(sessionStorage.getItem('melopa_utm') || '{}');
  } catch (e) {
    devWarn('Stored UTM data unreadable, using empty attribution:', e);
  }
  return {
    utm_source: utm.utm_source || null,
    utm_medium: utm.utm_medium || null,
    utm_campaign: utm.utm_campaign || null,
    utm_content: utm.utm_content || null,
    utm_term: utm.utm_term || null,
    landing_page: sessionStorage.getItem('melopa_landing_page') || window.location.pathname,
    device_type: getDeviceType(),
    source: 'website',
  };
}

// ---------------- Analytics ----------------
let settingsCache = null;

export async function fetchPublicSettings() {
  if (settingsCache) return settingsCache;
  try {
    const res = await axios.get(`${API}/settings/public`);
    settingsCache = res.data;
    return settingsCache;
  } catch (e) {
    return { ...DEFAULT_CONTACT, ga4_id: '', gtm_id: '', meta_pixel_id: '' };
  }
}

export function injectAnalytics(settings) {
  try {
    if (settings.gtm_id && !window.__melopaGTM) {
      window.__melopaGTM = true;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      const s = document.createElement('script');
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(settings.gtm_id)}`;
      document.head.appendChild(s);
    }
    if (settings.ga4_id && !window.__melopaGA4) {
      window.__melopaGA4 = true;
      const s = document.createElement('script');
      s.async = true;
      s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(settings.ga4_id)}`;
      document.head.appendChild(s);
      window.dataLayer = window.dataLayer || [];
      window.gtag = function gtag() {
        window.dataLayer.push(arguments);
      };
      window.gtag('js', new Date());
      window.gtag('config', settings.ga4_id);
      if (settings.google_ads_id) {
        window.gtag('config', settings.google_ads_id);
      }
    }
    if (settings.meta_pixel_id && !window.__melopaPixel) {
      window.__melopaPixel = true;
      /* eslint-disable */
      !(function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
      /* eslint-enable */
      window.fbq('init', settings.meta_pixel_id);
      window.fbq('track', 'PageView');
    }
  } catch (e) {
    // Analytics must never break the site — log and continue.
    devWarn('Analytics injection failed:', e);
  }
}

export function track(eventName, params = {}) {
  try {
    if (window.gtag) window.gtag('event', eventName, params);
    if (window.fbq) window.fbq('trackCustom', eventName, params);
    if (window.dataLayer) window.dataLayer.push({ event: eventName, ...params });
  } catch (e) {
    devWarn(`Tracking event "${eventName}" failed:`, e);
  }
}

// ---------------- WhatsApp ----------------
export function waLink(number, message) {
  return `https://wa.me/${number}?text=${encodeURIComponent(message)}`;
}

export function defaultWaMessage() {
  return "Hello MELOPA INFRA, I'd like to discuss a construction project.";
}

export function leadWaMessage(lead) {
  const location = [lead.city, lead.state].filter(Boolean).join(', ') || 'my location';
  const area = lead.built_up_area || 'area to be decided';
  const type = (lead.project_type || 'project').toLowerCase();
  return `Hello MELOPA INFRA, I just submitted a project enquiry on melopainfra.com for a ${type}, approximately ${area}, located in ${location}.`;
}

// ---------------- Device capability ----------------
export function prefersReducedMotion() {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {
    return false;
  }
}

export function supportsWebGL() {
  try {
    const canvas = document.createElement('canvas');
    return !!(window.WebGLRenderingContext && (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));
  } catch (e) {
    return false;
  }
}

export function isLowEndDevice() {
  try {
    const mem = navigator.deviceMemory;
    const cores = navigator.hardwareConcurrency;
    if (mem && mem <= 2) return true;
    if (cores && cores <= 2) return true;
    return false;
  } catch (e) {
    return false;
  }
}

export function use3DTier() {
  if (prefersReducedMotion() || !supportsWebGL() || isLowEndDevice()) return 'static';
  return 'full';
}
